import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import Card from 'components/DesignSystem/Card';
import EditButton from 'components/Shared/EditButton';
import RichHtml from 'components/RichHtml';
import SecaoDessemEstado from 'components/Relatorios/Dessem/secoes/shared/SecaoDessemEstado';
import PremissasEdicaoModal from 'components/Relatorios/Dessem/secoes/PremissasGerais/EdicaoModal';
import NiveisContingenciaCard from 'components/Relatorios/Dessem/secoes/PremissasGerais/NiveisContingencia';

import { selectUserIsAdmin } from 'redux/reducers/authReducer';
import useDessemSecaoParams from 'hooks/Relatorios/Dessem/useDessemSecaoParams';
import useRelatorioAprovacao from 'hooks/Relatorios/Aprovacao/useRelatorioAprovacao';
import { getAprovacaoConfig } from 'helper/Relatorios/Aprovacao/config';
import { NIVEIS_CONTINGENCIA_DESSEM } from 'helper/Relatorios/Dessem/niveisContingencia';
import { useGetDessemPremissasQuery } from 'services/api/products/relatorios';
import { DessemSecaoParams } from 'types/Relatorios/Dessem';

import './index.scss';

const config = getAprovacaoConfig('dessem');

/**
 * Botão de edição do texto do dia e o modal, montados só para o admin: o status de aprovação e o
 * estado do modal ficam aqui, fora do conteúdo da seção.
 */
function EdicaoDoTexto({ params }: { params: DessemSecaoParams }) {
  // Mesma consulta da faixa de contexto e da barra de revisão (cache compartilhado, sem requisição
  // nova). O botão só aparece com a etapa da tela EM REVISÃO: publicada, o texto não se edita;
  // em dia sem informe não há release (as rotas do editor respondem 404), como a barra de revisão
  // e o card de aprovação, que também somem. Enquanto a aprovação carrega, com erro ou com o fluxo
  // de aprovação desligado (query em `skip`), o status é desconhecido e o botão fica escondido; o
  // backend continua recusando com 409 de todo jeito.
  const { habilitado, status } = useRelatorioAprovacao(config);
  const [editando, setEditando] = useState(false);
  const abrirEdicao = useCallback(() => setEditando(true), []);
  const fecharEdicao = useCallback(() => setEditando(false), []);
  const mostrarBotao = habilitado && status?.status === 'em_revisao';

  // A caixa das ações reserva a altura do botão: o cabeçalho do card não muda de altura quando o
  // botão aparece (aprovação chegando depois da seção) ou some (troca para etapa publicada).
  return (
    <span className="dessem-premissas-gerais__acoes">
      {mostrarBotao && (
        <EditButton
          id="edit-dessem-premissas-gerais"
          onClick={abrirEdicao}
          aria-label="Editar Premissas Gerais"
          title="Editar Premissas Gerais"
        />
      )}
      <PremissasEdicaoModal open={editando} onClose={fecharEdicao} params={params} />
    </span>
  );
}

export default function PremissasGerais() {
  const userIsAdmin = useSelector(selectUserIsAdmin);
  const params = useDessemSecaoParams();

  const resultado = useGetDessemPremissasQuery(params);

  // O estado (spinner / sem informe / erro / conteúdo) fica DENTRO do card: o cabeçalho e o
  // botão de edição do admin continuam no lugar em qualquer caso. O título vem do payload; sem
  // ele (carregando, sem informe, erro) cai em "Premissas Gerais" (o `Card` põe em caixa alta).
  // Abaixo do card, o quadro fixo "Níveis de Contingência" (texto constante, sem consulta à API).
  return (
    <>
      <Card
        title={resultado.currentData?.titulo ?? 'Premissas Gerais'}
        className="h-100 dessem-premissas-gerais__card"
        actions={userIsAdmin && <EdicaoDoTexto params={params} />}
      >
        <SecaoDessemEstado resultado={resultado} params={params}>
          {(data) => (
            <RichHtml html={data.conteudo_html} className="w-100 ql-editor" />
          )}
        </SecaoDessemEstado>
      </Card>
      <NiveisContingenciaCard conteudo={NIVEIS_CONTINGENCIA_DESSEM} />
    </>
  );
}
